import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { doc, setDoc } from 'firebase/firestore';
// Import Google icon
import googleIcon from '../assets/google-icon.svg';

const Signup = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const { signup, signInWithGoogle } = useAuth();
  const navigate = useNavigate();

  const validatePassword = (password) => {
    const errors = [];
    
    if (password.length < 8) {
      errors.push('Password must be at least 8 characters long');
    }
    if (!/[A-Z]/.test(password)) {
      errors.push('Password must contain at least one uppercase letter');
    }
    if (!/\d/.test(password)) {
      errors.push('Password must contain at least one number');
    }
    
    return errors;
  };

  async function handleSubmit(e) {
    e.preventDefault();

    const passwordErrors = validatePassword(password);
    if (passwordErrors.length > 0) {
      setError(passwordErrors.join('\n'));
      return;
    }

    if (password !== confirmPassword) {
      return setError('Passwords do not match');
    }

    try {
      setError('');
      setLoading(true);

      // Create the user authentication account
      const userCredential = await signup(email, password);
      const user = userCredential.user;
      
      // Create a user document in Firestore with the same UID
      await createUserDocument(user);
      
      // Navigate to home page after successful signup and document creation
      navigate('/');
    } catch (error) {
      let errorMessage = 'Failed to create an account';
      
      if (error.code === 'auth/email-already-in-use') {
        errorMessage = 'This email address is already registered. Please use a different email or try logging in instead.';
      } else if (error.code === 'auth/weak-password') {
        errorMessage = 'Password is too weak. Please make sure it meets all requirements.';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'Please enter a valid email address.';
      } else {
        errorMessage += ': ' + error.message;
      }
      
      setError(errorMessage);
    }
    setLoading(false);
  }
  
  async function handleGoogleSignIn() {
    try {
      setError('');
      setGoogleLoading(true);
      
      // Sign in with Google
      const result = await signInWithGoogle();
      const user = result.user;
      
      // Create or update the user document in Firestore
      await createUserDocument(user);
      
      // Navigate to home page after successful sign-in
      navigate('/');
    } catch (error) {
      let errorMessage = 'Failed to sign in with Google';
      
      if (error.code === 'auth/popup-closed-by-user') {
        errorMessage = 'Sign-in was cancelled. Please try again.';
      } else if (error.code === 'auth/popup-blocked') {
        errorMessage = 'Sign-in popup was blocked by your browser. Please allow popups for this site.';
      } else {
        errorMessage += ': ' + error.message;
      }
      
      setError(errorMessage);
    }
    setGoogleLoading(false);
  }
  
  // Helper function to create or update user document in Firestore
  async function createUserDocument(user) {
    await setDoc(doc(db, 'users', user.uid), {
      uid: user.uid,
      email: user.email,
      createdAt: new Date(),
      // Default settings for the user
      defaultCapRate: '',
      defaultCoCReturn: '',
      defaultTotalROI: '',
      defaultManagementFee: '',
      defaultVacancyRate: '',
      defaultMaintenanceRate: '',
      defaultShortTermManagementFee: '',
      defaultOccupancyRate: '',
      defaultShortTermMaintenanceRate: '',
    }, { merge: true }); // Use merge to avoid overwriting existing data for returning users
  }

  const getPasswordStrength = (password) => {
    if (!password) return null;
    
    const errors = validatePassword(password);
    if (errors.length === 0) return { type: 'success', message: 'Password meets all requirements' };
    return { type: 'warning', message: errors.join('\n') };
  };

  const passwordStrength = getPasswordStrength(password);

  return (
    <div className="container">
      {/* Header with logo that links back to landing page */}
      <div className="row mt-4 mb-3">
        <div className="col">
          <Link to="/" className="text-decoration-none">
            <h4 className="text-light d-flex align-items-center">
              <i className="bi bi-house-door me-2"></i>
              <span>RE Calc</span>
            </h4>
          </Link>
        </div>
      </div>
      
      <div className="row justify-content-center min-vh-75 align-items-center">
        <div className="col-12 col-md-6 col-lg-4">
          <div className="card bg-dark border-secondary">
            <div className="card-body p-4">
              <h2 className="text-center mb-4">Sign Up</h2>
              
              {error && (
                <div className="alert alert-danger" role="alert" style={{ whiteSpace: 'pre-line' }}>
                  {error}
                </div>
              )}
              
              <form onSubmit={handleSubmit}>
                <div className="mb-3">
                  <label className="form-label text-muted">Email</label>
                  <input
                    type="email"
                    className="form-control bg-dark text-light border-secondary"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    required
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label text-muted">Password</label>
                  <input
                    type="password"
                    className={`form-control bg-dark text-light border-secondary ${
                      password && (passwordStrength?.type === 'success' ? 'is-valid' : 'is-invalid')
                    }`}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    required
                  />
                  {password && (
                    <div className={`${passwordStrength?.type === 'success' ? 'valid-feedback' : 'invalid-feedback'}`} style={{ whiteSpace: 'pre-line' }}>
                      {passwordStrength?.message}
                    </div>
                  )}
                </div>

                <div className="mb-4">
                  <label className="form-label text-muted">Confirm Password</label>
                  <input
                    type="password"
                    className={`form-control bg-dark text-light border-secondary ${
                      confirmPassword && (password === confirmPassword ? 'is-valid' : 'is-invalid')
                    }`}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm your password"
                    required
                  />
                  {confirmPassword && (
                    <div className={password === confirmPassword ? 'valid-feedback' : 'invalid-feedback'}>
                      {password === confirmPassword ? 'Passwords match' : 'Passwords do not match'}
                    </div>
                  )}
                </div>

                <button 
                  className="btn btn-primary w-100 mb-3" 
                  type="submit"
                  disabled={
                    loading ||
                    googleLoading ||
                    !email.trim() || 
                    !password.trim() || 
                    !confirmPassword.trim()
                  }
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Creating Account...
                    </>
                  ) : (
                    'Sign Up'
                  )}
                </button>
                
                <div className="d-flex align-items-center mb-3">
                  <hr className="flex-grow-1 border-secondary" />
                  <span className="mx-2 text-muted">or</span>
                  <hr className="flex-grow-1 border-secondary" />
                </div>
                
                <button 
                  type="button" 
                  className="btn btn-outline-light w-100 d-flex align-items-center justify-content-center mb-3" 
                  onClick={handleGoogleSignIn}
                  disabled={loading || googleLoading}
                >
                  {googleLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Signing in...
                    </>
                  ) : (
                    <>
                      <img src={googleIcon} alt="Google" className="me-2" style={{ width: '18px', height: '18px' }} />
                      Sign in with Google
                    </>
                  )}
                </button>
              </form>

              <div className="text-center auth-secondary-text">
                Already have an account? {' '}
                <Link to="/login" className="text-primary text-decoration-none">
                  Log In
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Signup;
